import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import IconButton from '@material-ui/core/IconButton/IconButton'
import moment from 'moment'
import ClearIcon from '@material-ui/icons/Clear'

import { WorkInProgressViewContainer } from './WorkInProgressView.styles'
import WipReportTable from './Components/WipReportTable'
import KeyDatesTable from './Components/KeyDatesTable'
import WipReportPreviewButton from './Components/WipReportPreviewButton'
import WorkInProgressReportPreview from './Components/WorkInProgressReportPreview'
import useWipData from './useWipData'
import { getKeyDatesByProjectSFId } from '../../entities/project/service'
import BudgetCards from './Components/BudgetCards'
import SummaryFinancialsTable from './Components/SummaryFinancialsTable'
import WipOrationEditor from './Components/WipOrationEditor'
import WipInternalNarrativeViewer from './Components/WipInternalNarrativeViewer'
import WipBudgetTrendDropdown from './Components/WipBudgetTrendDropdown'
import ApprovalProjectSelector from '../Approvals/Components/ApprovalProjectSelector'
import useFeatureFlags, { FeatureFlags } from '../../hooks/useFeatureFlags'
import StaffUtilizationTable from './Components/StaffUtilizationTable'
import useStaffUtilization from './lib/useStaffUtilization'
import WipSavedSummarySelect from './Components/WipSavedSummarySelect'
import {
  getWipReportAiSummary,
  listWipReportAiSummaries,
} from '../../entities/wipReportSummary/service'

const WorkInProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
`

document.body.style.overflow = 'auto'

const getCurrentMonthName = () => {
  const date = moment()
  return date.format('MMMM YYYY')
}

const WorkInProgressView = ({
  client,
  projects,
  person,
  invoiceTotals,
  selectedProject,
  onProjectSelect,
}) => {
  const [preview, setPreview] = useState()
  const [keyDates, setKeyDates] = useState([])

  const { isEnabled } = useFeatureFlags()
  const aiEnabled = isEnabled(FeatureFlags.ENABLE_WIP_REPORT_AI) === true

  const currentMonthName = useMemo(() => getCurrentMonthName(), [])
  const [oration, setOration] = useState('')
  const [internalNarrative, setInternalNarrative] = useState('')
  const [internalLoading, setInternalLoading] = useState(false)
  const [savedSummaries, setSavedSummaries] = useState([])
  const [selectedSummaryId, setSelectedSummaryId] = useState(null)
  const [summariesLoading, setSummariesLoading] = useState(false)

  const months = useMemo(() => {
    const current = moment()
    const year = current.year()
    const startMonth = current.month()
    const monthsArray = []

    for (let m = 0; m < 12; m++) {
      monthsArray.push(moment({ year, month: m }).format('YYYY-MM'))
    }

    return monthsArray.join(',')
  }, [])

  const selectedMonth = useMemo(() => {
    return moment().format('YYYY-MM')
  }, [])

  const projectsArrayWithOnlySelectedProject = useMemo(() => {
    return selectedProject ? [selectedProject] : []
  }, [selectedProject])

  const { selectedMonthProjectWipReport: wipReport, projectWipReports } = useWipData({
    client,
    projects: projectsArrayWithOnlySelectedProject,
    selectedProject,
    months,
    selectedMonth,
  })

  const {
    rows: staffUtilizationRows,
    loading: staffUtilizationLoading,
    error: staffUtilizationError,
  } = useStaffUtilization({
    client,
    projectId: selectedProject?._id,
    month: selectedMonth,
  })

  const loadSummaryById = useCallback(
    async summaryId => {
      if (!client || !summaryId) return
      setSummariesLoading(true)
      try {
        const summary = await getWipReportAiSummary(client, summaryId)
        if (summary) {
          setOration(summary.clientSummary || '')
          setInternalNarrative(summary.internalSummary || '')
          setSelectedSummaryId(summary.id)
        }
      } catch (e) {
        console.error(e)
      } finally {
        setSummariesLoading(false)
      }
    },
    [client],
  )

  const refreshSummaries = useCallback(
    async ({ selectNewest = false } = {}) => {
      if (!client || !selectedProject?._id || !selectedMonth) {
        setSavedSummaries([])
        setSelectedSummaryId(null)
        return []
      }
      setSummariesLoading(true)
      try {
        const items = await listWipReportAiSummaries(
          client,
          String(selectedProject._id),
          selectedMonth,
        )
        setSavedSummaries(items)
        if (selectNewest && items.length > 0) {
          const newest = items[items.length - 1]
          await loadSummaryById(newest.id)
        } else if (items.length > 0 && !selectNewest) {
          const newest = items[items.length - 1]
          setSelectedSummaryId(newest.id)
        }
        return items
      } catch (e) {
        console.error(e)
        setSavedSummaries([])
        return []
      } finally {
        setSummariesLoading(false)
      }
    },
    [client, selectedProject?._id, selectedMonth, loadSummaryById],
  )

  const handleSummaryGenerated = useCallback(async () => {
    await refreshSummaries({ selectNewest: true })
  }, [refreshSummaries])

  const handleSavedSummarySelect = useCallback(
    async summaryId => {
      await loadSummaryById(summaryId)
    },
    [loadSummaryById],
  )

  useEffect(() => {
    if (!selectedProject?._id || !client) {
      setOration('')
      setInternalNarrative('')
      setSavedSummaries([])
      setSelectedSummaryId(null)
      return
    }

    let cancelled = false

    const load = async () => {
      setOration('')
      setInternalNarrative('')
      setSelectedSummaryId(null)
      setSummariesLoading(true)
      try {
        const items = await listWipReportAiSummaries(
          client,
          String(selectedProject._id),
          selectedMonth,
        )
        if (cancelled) return
        setSavedSummaries(items)
        if (items.length > 0) {
          const newest = items[items.length - 1]
          const summary = await getWipReportAiSummary(client, newest.id)
          if (cancelled) return
          if (summary) {
            setOration(summary.clientSummary || '')
            setInternalNarrative(summary.internalSummary || '')
            setSelectedSummaryId(summary.id)
          }
        }
      } catch (e) {
        if (!cancelled) console.error(e)
      } finally {
        if (!cancelled) setSummariesLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [selectedProject?._id, selectedMonth, client])

  useEffect(() => {
    if (!selectedProject) return

    const fetchKeyDates = async () => {
      const result = await getKeyDatesByProjectSFId(client, selectedProject.salesforce_id)

      if (result) {
        setKeyDates(result.keyDates)
      }
    }

    fetchKeyDates()
  }, [selectedProject])

  const [budgetTrend, setBudgetTrend] = useState('On Target')

  const invoices = useMemo(() => {
    const currentMonth = moment().startOf('month')

    const filtered = invoiceTotals.filter(({ month }) => {
      const m = moment(month, 'MMMM YYYY')
      return m.isBefore(currentMonth)
    })

    const map = new Map()
    for (const { month, total } of filtered) {
      map.set(month, (map.get(month) || 0) + total)
    }

    const deduped = Array.from(map, ([month, total]) => ({ month, total }))

    const sliced = deduped.slice(0, 5)

    return sliced.reverse()
  }, [invoiceTotals])

  if (!selectedProject)
    return (
      <WorkInProgressContainer>
        <ApprovalProjectSelector
          projects={projects}
          selectedProject={selectedProject}
          onSelectProject={onProjectSelect}
        />
      </WorkInProgressContainer>
    )

  return wipReport ? (
    <WorkInProgressContainer>
      {!preview && (
        <WorkInProgressViewContainer>
          <div style={{ width: '100%' }}>
            <h2 style={{ textAlign: 'center' }} data-testid="wip.reportHeader">
              {selectedProject.name} Value Delivery Review Report - Current Month to Date:{' '}
              {currentMonthName}
            </h2>

            <BudgetCards
              budget={wipReport.budget}
              pacing={wipReport.wipPacing}
              currency={selectedProject.CurrencyIsoCode}
            />

            <h2 style={{ textAlign: 'left', marginTop: 40 }}>Summary</h2>

            <SummaryFinancialsTable
              monthlyWipReports={projectWipReports?.monthlyWipReports}
              invoiceTotals={invoices}
              pacing={wipReport.wipPacing}
              currency={selectedProject.CurrencyIsoCode}
              showCurrentMonthValue
              wipReport={wipReport}
            />

            <WipBudgetTrendDropdown onChange={val => setBudgetTrend(val)} />

            {aiEnabled && (
              <WipSavedSummarySelect
                items={savedSummaries}
                selectedId={selectedSummaryId}
                onSelect={handleSavedSummarySelect}
                disabled={summariesLoading}
              />
            )}

            <WipOrationEditor
              client={client}
              projectId={selectedProject._id}
              month={selectedMonth}
              value={oration}
              onChange={setOration}
              onInternalChange={setInternalNarrative}
              onInternalLoadingChange={setInternalLoading}
              onGenerated={handleSummaryGenerated}
            />

            {aiEnabled && (
              <WipInternalNarrativeViewer narrative={internalNarrative} loading={internalLoading} />
            )}

            {aiEnabled && (
              <StaffUtilizationTable
                rows={staffUtilizationRows}
                iso={selectedProject.CurrencyIsoCode}
                loading={staffUtilizationLoading}
                error={staffUtilizationError}
              />
            )}

            <WipReportTable project={selectedProject} wipData={wipReport} />

            <div style={{ width: '70%', margin: 'auto' }}>
              <KeyDatesTable keyDates={keyDates} title="Key Dates" />
            </div>
          </div>

          <WipReportPreviewButton onClick={() => setPreview(true)} />
        </WorkInProgressViewContainer>
      )}

      {preview && (
        <WorkInProgressReportPreview
          project={selectedProject}
          onReturn={() => setPreview(null)}
          month={currentMonthName}
          person={person}
          wipData={wipReport}
          client={client}
          invoiceTotals={invoices}
          oration={oration}
          monthlyWipReports={projectWipReports?.monthlyWipReports}
          currency={selectedProject.CurrencyIsoCode}
          pacing={wipReport?.wipPacing || 0}
          keyDates={keyDates}
          budget={wipReport.budget}
          budgetTrend={budgetTrend}
        />
      )}
      {preview && (
        <div style={{ position: 'absolute', top: 0 }}>
          <IconButton
            onClick={() => setPreview(false)}
            name="go back"
            data-testid="wip.previewClose"
          >
            <ClearIcon />
          </IconButton>
        </div>
      )}
    </WorkInProgressContainer>
  ) : null
}

WorkInProgressView.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object),
}

export { WorkInProgressView }
export default WorkInProgressView
